import { MultiSelect } from '@ui';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { ColonyListItem } from '../api';

export interface ColonyPickerProps {
  colonies: ColonyListItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
  error?: string | undefined;
  required?: boolean;
  /** Location chosen elsewhere in the form: its colonies come first (#81). */
  locationId?: string | undefined;
}

/**
 * Choose colonies grouped by location, with "all at this location" (VOL-007):
 * one row with a summary in the form, the list in a sheet (#81). Only active
 * colonies are offered, plus inactive ones that are already selected.
 */
export function ColonyPicker({
  colonies,
  selected,
  onChange,
  error,
  required = false,
  locationId,
}: ColonyPickerProps) {
  const { t } = useTranslation();
  const offered = colonies.filter((c) => c.status === 'active' || selected.includes(c.id));
  return (
    <MultiSelect
      label={t('apiary.picker.label')}
      title={t('apiary.picker.title')}
      summary={summary(offered, selected, t)}
      options={offered.map((c) => ({
        id: c.id,
        label: c.name,
        group: { id: c.location.id, label: c.location.name },
      }))}
      selected={selected}
      onChange={onChange}
      firstGroup={locationId}
      required={required}
      error={error}
      empty={t('apiary.picker.none')}
      labels={{
        search: t('apiary.picker.search'),
        clearSearch: t('common.clear'),
        noMatch: t('common.noResults'),
        countOf: (count, total) => t('apiary.picker.countOf', { count, total }),
        selected: (count) => t('common.selected', { count }),
        selectAll: t('apiary.picker.selectAll'),
        selectNone: t('apiary.picker.selectNone'),
        done: t('apiary.picker.done'),
        cancel: t('common.cancel'),
        close: t('common.close'),
      }}
    />
  );
}

/** "Keine", "Berta, Frieda", "Alle 8 an Obstwiese" or "12 Völker". */
function summary(colonies: ColonyListItem[], selected: string[], t: TFunction): string {
  const chosen = colonies.filter((c) => selected.includes(c.id));
  if (chosen.length === 0) return t('apiary.picker.noneSelected');
  const location = chosen[0]?.location;
  if (location && chosen.length > 1 && chosen.every((c) => c.location.id === location.id)) {
    const all = colonies.filter((c) => c.location.id === location.id);
    if (all.length === chosen.length) {
      return t('apiary.picker.allAtSummary', { count: chosen.length, location: location.name });
    }
  }
  if (chosen.length <= 3) return chosen.map((c) => c.name).join(', ');
  return t('apiary.picker.count', { count: chosen.length });
}
